/**
 * NetworkPanel — открытые порты, активные соединения, firewall (ufw + iptables).
 *
 * Backend: /api/v1/devops/network/{listen,connections,firewall}.
 */

import { useEffect, useState } from "react";
import { RefreshCw, Loader2, Search, Network, Shield } from "lucide-react";
import {
  fetchNetworkListen,
  fetchNetworkConnections,
  fetchFirewall,
  type NativeListenEntry,
  type NativeConnectionEntry,
} from "../../lib/api";
import { cn } from "../../lib/cn";

type Section = "listen" | "connections" | "firewall";

export function NetworkPanel() {
  const [section, setSection] = useState<Section>("listen");

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        <SectionBtn
          active={section === "listen"}
          onClick={() => setSection("listen")}
          icon={<Network className="size-3.5" />}
          label="Открытые порты"
        />
        <SectionBtn
          active={section === "connections"}
          onClick={() => setSection("connections")}
          icon={<Network className="size-3.5" />}
          label="Соединения"
        />
        <SectionBtn
          active={section === "firewall"}
          onClick={() => setSection("firewall")}
          icon={<Shield className="size-3.5" />}
          label="Firewall"
        />
      </div>
      {section === "listen" && <ListenSection />}
      {section === "connections" && <ConnectionsSection />}
      {section === "firewall" && <FirewallSection />}
    </div>
  );
}

function SectionBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md text-small inline-flex items-center gap-1.5 border transition-colors",
        active
          ? "bg-accent-500/10 text-accent-500 border-accent-500/40"
          : "border-border/60 text-fg-muted hover:bg-bg-subtle",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function ListenSection() {
  const [items, setItems] = useState<NativeListenEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  async function load() {
    try {
      setError(null);
      const r = await fetchNetworkListen();
      setItems(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter(
    (it) =>
      !filter ||
      String(it.port).includes(filter) ||
      it.address.includes(filter) ||
      it.process.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Порт, адрес, процесс…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small focus:outline-none focus:border-accent-500"
          />
        </div>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded-md border border-border/60 hover:bg-bg-subtle text-small text-fg-muted"
        >
          <RefreshCw className="size-3.5" />
        </button>
        <span className="text-small text-fg-muted ml-auto">{filtered.length} портов</span>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead className="bg-bg-subtle/60">
              <tr className="text-left text-fg-subtle text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2">PROTO</th>
                <th className="px-3 py-2">ADDRESS</th>
                <th className="px-3 py-2 text-right">PORT</th>
                <th className="px-3 py-2">PROCESS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-fg-muted">
                    <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка…
                  </td>
                </tr>
              ) : (
                filtered.map((it, i) => (
                  <tr key={`${it.proto}-${it.address}-${it.port}-${i}`} className="border-t border-border/30 hover:bg-bg-subtle/30">
                    <td className="px-3 py-1.5">
                      <span
                        className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] uppercase font-bold",
                          it.proto === "tcp" ? "bg-accent-500/15 text-accent-500" : "bg-violet/15 text-violet",
                        )}
                      >
                        {it.proto}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-fg-muted">{it.address}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-fg">{it.port}</td>
                    <td className="px-3 py-1.5 text-fg-muted truncate max-w-[400px]">{it.process || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ConnectionsSection() {
  const [items, setItems] = useState<NativeConnectionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState("established");

  async function load() {
    try {
      setError(null);
      setLoading(true);
      const r = await fetchNetworkConnections({ state, limit: 500 });
      setItems(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [state]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select
          value={state}
          onChange={(e) => setState(e.target.value)}
          className="px-2 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small"
        >
          <option value="established">established</option>
          <option value="all">all</option>
          <option value="syn_sent">syn-sent</option>
          <option value="time_wait">time-wait</option>
          <option value="close_wait">close-wait</option>
        </select>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded-md border border-border/60 hover:bg-bg-subtle text-small text-fg-muted"
        >
          <RefreshCw className="size-3.5" />
        </button>
        <span className="text-small text-fg-muted ml-auto">{items.length} соединений</span>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead className="bg-bg-subtle/60">
              <tr className="text-left text-fg-subtle text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2">STATE</th>
                <th className="px-3 py-2">LOCAL</th>
                <th className="px-3 py-2">PEER</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-fg-muted"><Loader2 className="size-4 animate-spin inline mr-2"/></td></tr>
              ) : (
                items.map((c, i) => (
                  <tr key={i} className="border-t border-border/30 hover:bg-bg-subtle/30">
                    <td className="px-3 py-1.5 text-fg-muted">{c.state}</td>
                    <td className="px-3 py-1.5 text-fg">{c.local}</td>
                    <td className="px-3 py-1.5 text-fg">{c.peer}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FirewallSection() {
  const [data, setData] = useState<{
    ufw: { success: boolean; output: string; stderr: string };
    iptables: { success: boolean; output: string; stderr: string };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setError(null);
      const r = await fetchFirewall();
      setData(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={load}
        className="px-2 py-1.5 rounded-md border border-border/60 hover:bg-bg-subtle text-small text-fg-muted"
      >
        <RefreshCw className="size-3.5 inline mr-1" />
        Обновить
      </button>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      {loading && <div className="text-fg-muted text-small"><Loader2 className="size-4 animate-spin inline mr-2" /> загрузка…</div>}
      {data && (
        <div className="grid lg:grid-cols-2 gap-3">
          <Pane title="UFW" output={data.ufw.output || data.ufw.stderr || "(пусто)"} />
          <Pane title="iptables -L INPUT" output={data.iptables.output || data.iptables.stderr || "(пусто)"} />
        </div>
      )}
    </div>
  );
}

function Pane({ title, output }: { title: string; output: string }) {
  return (
    <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
      <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 text-[11px] uppercase tracking-wider font-mono text-fg-subtle">
        {title}
      </div>
      <pre className="p-3 text-[11px] font-mono whitespace-pre-wrap leading-relaxed text-fg max-h-[60vh] overflow-auto">
        {output}
      </pre>
    </div>
  );
}
